import { useEffect } from "react"

import { useQuery } from "@tanstack/react-query"

import { supabase } from "../lib/supabase"
import { DateFilter } from "../types/filters"

/**
 * Interface for the simplified stage progression response
 * Maps stage names to the count of unique applications in each stage
 */
export interface StageProgressionResponse {
  stageCounts: Record<string, number>
}

/**
 * Hook to fetch application stage progression data from the metrics-stage-progression endpoint
 * @param dateFilter Optional date range filter
 * @returns Query result with stage counts data (number of unique applications per stage)
 */
export const useStageProgressionData = (dateFilter?: DateFilter, jobId?: string) => {
  const query = useQuery<StageProgressionResponse>({
    queryKey: ["stageProgression", dateFilter, jobId ?? "all"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("metrics-stage-progression", {
        body: {
          startDate: dateFilter?.startDate || null,
          endDate: dateFilter?.endDate || null,
          jobId: jobId ?? null,
        },
      })

      if (error) {
        throw new Error(`Error fetching stage progression data: ${error.message}`)
      }

      // Return the new response format with stageCounts
      return data as StageProgressionResponse
    },
    // Keep data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Enable refetch on window focus
    refetchOnWindowFocus: true,
  })

  // Effect to refetch data when component mounts/tab becomes active
  useEffect(() => {
    // Refetch data when component mounts
    query.refetch()

    // Set up event listener for visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        query.refetch()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Clean up event listener on unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [query])

  return query
}
